import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  Divider,
  IconButton,
  InputAdornment,
  LinearProgress,
  Chip,
  Stack,
  Container,
  Paper
} from '@mui/material';
import {
  Visibility,
  VisibilityOff
} from '@mui/icons-material';
import { apiCall } from '../utils/api';
import GoogleSignInButton from './GoogleSignInButton';
import FacebookSignInButton from './FacebookSignInButton';
import GitHubSignInButton from './GitHubSignInButton';

const Login = ({ onLogin }) => {
  const [isSignUp, setIsSignUp] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    password: '',
    confirmPassword: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({ score: 0, feedback: [] });

  const validatePassword = (password) => {
    const checks = [
      { test: /.{8,}/, message: 'At least 8 characters' },
      { test: /[A-Z]/, message: 'One uppercase letter' },
      { test: /[a-z]/, message: 'One lowercase letter' },
      { test: /\d/, message: 'One number' },
      { test: /[!@#$%^&*(),.?":{}|<>]/, message: 'One special character' }
    ];
    
    const passed = checks.filter(check => check.test.test(password));
    const failed = checks.filter(check => !check.test.test(password));
    
    return {
      score: passed.length,
      feedback: failed.map(check => check.message)
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      if (isSignUp) {
        // Signup validation
        if (formData.password !== formData.confirmPassword) {
          setError('Passwords do not match');
          setIsLoading(false);
          return;
        }
        
        if (passwordStrength.score < 5) {
          setError('Please meet all password requirements');
          setIsLoading(false);
          return;
        }

        const data = await apiCall('/api/auth/signup', {
          method: 'POST',
          body: JSON.stringify({
            email: formData.email,
            name: formData.name,
            password: formData.password
          }),
        });

        // Store token in localStorage
        localStorage.setItem('auth_token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        localStorage.setItem('families', JSON.stringify(data.families));
        
        onLogin(data);
      } else {
        // Login
        const data = await apiCall('/api/auth/login', {
          method: 'POST',
          body: JSON.stringify({
            email: formData.email,
            password: formData.password
          }),
        });

        // Store token in localStorage
        localStorage.setItem('auth_token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        localStorage.setItem('families', JSON.stringify(data.families));
        
        onLogin(data);
      }
    } catch (error) {
      setError(error.message || (isSignUp ? 'Signup failed' : 'Login failed'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });

    // Update password strength for signup
    if (name === 'password' && isSignUp) {
      setPasswordStrength(validatePassword(value));
    }
  };

  const handleGoogleLogin = async (tokenResponse) => {
    console.log('Starting Google login process...');
    setIsLoading(true);
    setError('');
    
    try {
      if (!tokenResponse.access_token) {
        throw new Error('No access token received from Google');
      }

      console.log('Sending Google access token to backend...');
      const data = await apiCall('/api/auth/google', {
        method: 'POST',
        body: JSON.stringify({
          access_token: tokenResponse.access_token
        }),
      });

      console.log('Google login successful:', data);

      // Store token in localStorage
      localStorage.setItem('auth_token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      localStorage.setItem('families', JSON.stringify(data.families));
      
      onLogin(data);
    } catch (error) {
      console.error('Google login error:', error);
      setError(error.message || 'Google login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFacebookLogin = async (facebookResponse) => {
    console.log('Starting Facebook login process...');
    setIsLoading(true);
    setError('');
    
    try {
      if (!facebookResponse.access_token) {
        throw new Error('No access token received from Facebook');
      }

      console.log('Sending Facebook token to backend...');
      const data = await apiCall('/api/auth/facebook', {
        method: 'POST',
        body: JSON.stringify({
          access_token: facebookResponse.access_token
        }),
      });

      console.log('Facebook login successful:', data);

      // Store token in localStorage
      localStorage.setItem('auth_token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      localStorage.setItem('families', JSON.stringify(data.families));
      
      onLogin(data);
    } catch (error) {
      console.error('Facebook login error:', error);
      setError(error.message || 'Facebook login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGitHubLogin = async (githubResponse) => {
    console.log('Starting GitHub login process...');
    console.log('GitHub response received:', githubResponse);
    setIsLoading(true);
    setError('');
    
    try {
      // The GitHub OAuth flow now returns complete user data
      if (!githubResponse || !githubResponse.token) {
        throw new Error('No authentication data received from GitHub OAuth');
      }

      console.log('GitHub OAuth successful, user data received:', githubResponse);

      // Store token in localStorage
      localStorage.setItem('auth_token', githubResponse.token);
      localStorage.setItem('user', JSON.stringify(githubResponse.user));
      localStorage.setItem('families', JSON.stringify(githubResponse.families));
      
      onLogin(githubResponse);
    } catch (error) {
      console.error('GitHub login error:', error);
      setError(error.message || 'GitHub login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const toggleMode = () => {
    setIsSignUp(!isSignUp);
    setError('');
    setFormData({
      email: formData.email, // Keep email when switching
      name: '',
      password: '',
      confirmPassword: ''
    });
    setPasswordStrength({ score: 0, feedback: [] });
  };

  const getPasswordStrengthColor = (score) => {
    if (score <= 1) return 'error';
    if (score <= 2) return 'warning';
    if (score <= 3) return 'info';
    if (score <= 4) return 'success';
    return 'success';
  };

  const getPasswordStrengthText = (score) => {
    if (score === 0) return 'Very weak';
    if (score === 1) return 'Weak';
    if (score === 2) return 'Fair';
    if (score === 3) return 'Good';
    if (score === 4) return 'Strong';
    return 'Very strong';
  };

  return (
    <Container maxWidth="sm">
      <Box sx={{ py: 4 }}>
        <Card elevation={3}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Typography variant="h3" component="h1" gutterBottom>
                Life Runbooks
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {isSignUp ? 'Create your account to get started' : 'Sign in to manage your family\'s emergency procedures'}
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit} sx={{ mb: 3 }}>
              <Stack spacing={3}>
                <TextField
                  fullWidth
                  label="Email Address"
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="Enter your email"
                  autoComplete="email"
                />

                {isSignUp && (
                  <TextField
                    fullWidth
                    label="Full Name"
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Enter your full name"
                    autoComplete="name"
                  />
                )}

                <TextField
                  fullWidth
                  label="Password"
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  placeholder={isSignUp ? "Create a strong password" : "Enter your password"}
                  autoComplete={isSignUp ? "new-password" : "current-password"}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                {isSignUp && formData.password && (
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Password Requirements:
                    </Typography>
                    <Stack spacing={1}>
                      {[
                        { test: /^.{8,}$/, label: 'At least 8 characters' },
                        { test: /[A-Z]/, label: 'One uppercase letter (A-Z)' },
                        { test: /[a-z]/, label: 'One lowercase letter (a-z)' },
                        { test: /\d/, label: 'One number (0-9)' },
                        { test: /[!@#$%^&*(),.?":{}|<>]/, label: 'One special character (!@#$%^&*)' }
                      ].map((check, index) => (
                        <Chip
                          key={index}
                          label={check.label}
                          size="small"
                          color={check.test.test(formData.password) ? 'success' : 'default'}
                          variant={check.test.test(formData.password) ? 'filled' : 'outlined'}
                        />
                      ))}
                    </Stack>
                    
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2" gutterBottom>
                        Password strength: {getPasswordStrengthText(passwordStrength.score)}
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={(passwordStrength.score / 5) * 100}
                        color={getPasswordStrengthColor(passwordStrength.score)}
                        sx={{ height: 8, borderRadius: 4 }}
                      />
                    </Box>
                  </Paper>
                )}

                {isSignUp && (
                  <TextField
                    fullWidth
                    label="Confirm Password"
                    type={showConfirmPassword ? "text" : "password"}
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    required
                    placeholder="Confirm your password"
                    autoComplete="new-password"
                    error={formData.confirmPassword && formData.password !== formData.confirmPassword}
                    helperText={formData.confirmPassword && formData.password !== formData.confirmPassword ? 'Passwords do not match' : ''}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            edge="end"
                          >
                            {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                )}

                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  fullWidth
                  disabled={isLoading}
                  sx={{ py: 1.5 }}
                >
                  {isLoading ? (isSignUp ? 'Creating Account...' : 'Signing In...') : (isSignUp ? 'Create Account' : 'Sign In')}
                </Button>
              </Stack>
            </Box>

            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Typography variant="body2" color="text.secondary">
                {isSignUp ? 'Already have an account?' : 'Don\'t have an account?'}
                <Button
                  variant="text"
                  onClick={toggleMode}
                  sx={{ ml: 1 }}
                >
                  {isSignUp ? 'Sign In' : 'Sign Up'}
                </Button>
              </Typography>
            </Box>

            <Divider sx={{ my: 3 }}>
              <Typography variant="body2" color="text.secondary">
                Or continue with
              </Typography>
            </Divider>

            <Stack spacing={2} sx={{ mb: 3 }}>
              <GoogleSignInButton
                onSuccess={handleGoogleLogin}
                onError={(error) => setError(error)}
                disabled={isLoading}
              />
              
              <FacebookSignInButton
                onSuccess={handleFacebookLogin}
                onError={(error) => setError(error)}
                disabled={isLoading}
              />
              
              <GitHubSignInButton
                onSuccess={handleGitHubLogin}
                onError={(error) => setError(error)}
                disabled={isLoading}
              />
            </Stack>

            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary">
                By {isSignUp ? 'creating an account' : 'signing in'}, you agree to our terms of service and privacy policy.
                Your family's runbooks are private and secure.
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default Login;